// SheEarnsInnovationHub.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faRocket,
  faTools,
  faComments,
  faBoxOpen,
  faHandshake,
  faGavel,
  faUsers,
  faRobot,
  faCommentsDollar,
  faCheckCircle,
  faRupeeSign,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

/**
 * SheEarnsInnovationHub.jsx
 * - 10 portlets for Innovation Hub (functional dummy implementations).
 * - Mobile-first responsive design.
 * - Theme: Darker pink (pink-800/900 backgrounds, pink-600 accents, light pink texts).
 * - Refactored for Women, Home, Children, Family & General Innovation.
 */

const Portlet = ({ icon, title, desc, expanded, onToggle, children }) => {
  return (
    <div className="bg-gray-900 border border-pink-600 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col items-center p-4 sm:p-6">
      <button
        onClick={onToggle}
        className="py-3 px-4 sm:py-4 sm:px-6 flex flex-col items-center gap-2 sm:gap-3 rounded-xl bg-pink-700 hover:bg-pink-600 transition w-full"
        aria-expanded={expanded}
      >
        <div className="flex-shrink-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-pink-50 text-pink-600 flex items-center justify-center text-lg sm:text-xl">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>

        <div className="w-full">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-pink-100">{title}</h3>
              <p className="text-xs sm:text-sm text-white mt-1">{desc}</p>
            </div>
            <div className="px-2 py-1 sm:px-4 sm:py-2 rounded-lg text-xs bg-pink-500 text-white">{expanded ? "Collapse" : "Open"}</div>
          </div>
        </div>
      </button>

      {expanded && <div className="p-4 sm:p-5 border-t border-pink-400 bg-pink-50/20 w-full">{children}</div>}
    </div>
  );
};

/* -------------------- Subcomponents for portlets -------------------- */

/* 1. Knowledge Zone - search + short articles */
const KnowledgeZone = () => {
  const articles = [
    { id: 1, title: "Home Crafting 101", tag: "Basics", snippet: "Step-by-step guide to simple home-based crafting for women." },
    { id: 2, title: "Child Nutrition Essentials", tag: "Children", snippet: "Tips for balanced meals and healthy growth for kids." },
    { id: 3, title: "Family Budgeting Tools", tag: "Family", snippet: "Practical ways to manage household finances effectively." },
  ];
  const [q, setQ] = useState("");
  const results = articles.filter((a) => a.title.toLowerCase().includes(q.toLowerCase()) || a.tag.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="w-full">
      <div className="mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge (e.g., crafting, nutrition)" className="w-full p-2 border border-pink-300 rounded text-black" />
      </div>

      <div className="space-y-2">
        {results.map((a) => (
          <div key={a.id} className="bg-white p-3 rounded border">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
              <div>
                <h4 className="font-semibold text-pink-700">{a.title}</h4>
                <p className="text-sm text-gray-500">{a.tag}</p>
                <p className="text-sm text-gray-700 mt-1">{a.snippet}</p>
              </div>
              <button className="sm:ml-3 text-sm text-white border border-pink-200 px-3 py-1 rounded bg-pink-600">Read</button>
            </div>
          </div>
        ))}
        {results.length === 0 && <div className="text-sm text-gray-500">No articles found.</div>}
      </div>
    </div>
  );
};

/* 2. Innovative Products (0-2 years) - marketplace */
const InnovativeProducts = () => {
  const [products] = useState([
    { id: 1, name: "Smart Home Organizer v2", price: 45000, ageMonths: 10, rating: 4.5, desc: "Compact storage solution for busy homes." },
    { id: 2, name: "Child Learning Tablet", price: 15000, ageMonths: 6, rating: 4.2, desc: "Interactive educational device for kids." },
  ]);
  const [wish, setWish] = useState([]);
  const toggleWish = (p) => setWish((w) => (w.includes(p.id) ? w.filter((id) => id !== p.id) : [...w, p.id]));

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white p-3 rounded border">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
              <div className="flex-1">
                <h4 className="font-semibold text-pink-700">{p.name}</h4>
                <p className="text-sm text-gray-500">{p.desc}</p>
                <div className="mt-2 text-sm text-gray-600">
                  Launched {p.ageMonths} months ago • <span className="text-pink-600"><FontAwesomeIcon icon={faStar} /> {p.rating}</span>
                </div>
              </div>
              <div className="text-right sm:text-left">
                <div className="text-pink-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {p.price}</div>
                <button onClick={() => toggleWish(p)} className="w-full sm:w-32 mt-2 px-3 py-1 rounded border text-sm bg-pink-600 text-white">{wish.includes(p.id) ? "Wishlisted" : "Wishlist"}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 3. Innovative Services - list and request service */
const InnovativeServices = () => {
  const services = [
    { id: 1, name: "Home Tutoring Service", pricePerSession: 1500, desc: "Personalized online tutoring for children." },
    { id: 2, name: "Family Wellness Check", pricePerSession: 300, desc: "Virtual health consultations for the family." },
  ];
  const [requests, setRequests] = useState([]);
  const requestService = (s) => {
    const contact = prompt(`Enter contact for requesting ${s.name} (dummy):`);
    if (!contact) return;
    setRequests((r) => [...r, { id: Date.now(), service: s.name, contact }]);
    alert(`Service request sent for ${s.name} (dummy)`);
  };

  return (
    <div className="w-full">
      <div className="space-y-2">
        {services.map((s) => (
          <div key={s.id} className="bg-white p-3 rounded border flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
            <div className="flex-1">
              <h4 className="font-semibold text-pink-700">{s.name}</h4>
              <p className="text-sm text-gray-500">{s.desc}</p>
            </div>
            <div className="text-right sm:text-left">
              <div className="text-pink-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {s.pricePerSession}</div>
              <button onClick={() => requestService(s)} className="w-full sm:w-auto mt-2 bg-pink-600 text-white px-3 py-1 rounded">Request</button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold text-pink-700">Requests (dummy)</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No requests yet.</p> : requests.map((r) => <div key={r.id} className="text-sm">{r.service} • {r.contact}</div>)}
      </div>
    </div>
  );
};

/* 4. Women Feedback & Field Trials (post + gallery) */
const FeedbackTrials = () => {
  const [trials, setTrials] = useState([
    { id: 1, user: "Rani", product: "Child Learning Tablet", summary: "Engaged kids daily, improved learning outcomes", pics: [] },
  ]);
  const [form, setForm] = useState({ user: "", product: "", summary: "" });

  const submit = (e) => {
    e.preventDefault();
    setTrials((t) => [...t, { ...form, id: Date.now(), pics: [], user: form.user }]);
    setForm({ user: "", product: "", summary: "" });
    alert("Feedback posted (dummy)");
  };

  return (
    <div className="w-full">
      <form onSubmit={submit} className="bg-pink-800 p-3 rounded mb-3">
        <input required value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} placeholder="Your name" className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <input required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Product tried" className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <textarea required value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Short summary" className="w-full p-2 border border-pink-300 rounded mb-2 bg-pink-100 text-black" style={{height: "100px", width: "100%"}} />
        <button className="w-full bg-pink-600 text-white py-2 rounded">Post Feedback</button>
      </form>

      <div className="space-y-2">
        {trials.map((t) => (
          <div key={t.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold text-pink-700">{t.product} — <span className="text-sm text-gray-500">{t.user}</span></h4>
                <p className="text-sm text-gray-700 mt-1">{t.summary}</p>
              </div>
              <div className="text-xs text-gray-500">Photos: {t.pics.length}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 5. Sample Ask & Delivery (request sample, track status) */
const SampleAsk = () => {
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", product: "", address: "" });

  const submit = (e) => {
    e.preventDefault();
    const id = Date.now();
    setRequests((r) => [...r, { ...form, id, status: "Requested" }]);
    setForm({ name: "", product: "", address: "" });
    alert("Sample requested (dummy)");
  };

  const progress = (id) => {
    setRequests((r) => r.map((req) => (req.id === id ? { ...req, status: req.status === "Delivered" ? "Delivered" : "Dispatched" } : req)));
  };

  return (
    <div className="w-full">
      <form onSubmit={submit} className="bg-white p-3 rounded border mb-3">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <input required placeholder="Product (sample)" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <input required placeholder="Delivery address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <button className="w-full bg-pink-600 text-white py-2 rounded">Request Sample</button>
      </form>

      <div className="bg-white p-3 rounded border">
        <h5 className="font-semibold text-pink-700">Sample Requests</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No sample requests yet.</p> : requests.map((r) => (
          <div key={r.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 border-b text-sm gap-2">
            <div>{r.product} • {r.name}</div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-600">{r.status}</div>
              <button onClick={() => progress(r.id)} className="text-sm text-pink-600">Advance</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 6. Innovation Accelerator - pitch submission + investors list */
const Accelerator = () => {
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ founder: "", idea: "", contact: "" });
  const investors = [
    { id: 1, name: "WomenEmpower VC", focus: "Early-stage women-led innovations" },
    { id: 2, name: "FamilyTech Fund", focus: "Home and family sustainability startups" },
  ];

  const submit = (e) => {
    e.preventDefault();
    setSubmissions((s) => [...s, { ...form, id: Date.now() }]);
    setForm({ founder: "", idea: "", contact: "" });
    alert("Pitch submitted (dummy)");
  };

  return (
    <div className="w-full">
      <form onSubmit={submit} className="bg-pink-800 p-3 rounded mb-3">
        <input required placeholder="Founder / Team" value={form.founder} onChange={(e) => setForm({ ...form, founder: e.target.value })} className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <input required placeholder="Idea / Product name" value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <input required placeholder="Contact email/phone" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="w-full p-2 border border-pink-300 rounded mb-2 text-black" />
        <button className="w-full bg-pink-600 text-white py-2 rounded">Submit Pitch</button>
      </form>

      <div className="bg-white p-3 rounded border mb-3">
        <h5 className="font-semibold text-pink-700">Investor Partners</h5>
        {investors.map((inv) => (
          <div key={inv.id} className="flex flex-col sm:flex-row sm:justify-between text-sm py-1 border-b gap-2">
            <div>{inv.name}</div>
            <div className="text-gray-500">{inv.focus}</div>
          </div>
        ))}
      </div>

      <div className="bg-white p-3 rounded border">
        <h5 className="font-semibold text-pink-700">Submissions (dummy)</h5>
        {submissions.length === 0 ? <p className="text-sm text-gray-500">No submissions yet.</p> : submissions.map((s) => <div key={s.id} className="text-sm border-b py-1">{s.founder} • {s.idea}</div>)}
      </div>
    </div>
  );
};

/* 7. Policy & Schemes corner (list) */
const PolicyCorner = () => {
  const policies = [
    { id: 1, title: "Women Empowerment Subsidy", snippet: "New guidelines for home-based business adoption." },
    { id: 2, title: "Child Welfare Support Scheme", snippet: "Assistance for family education and health programs." },
  ];
  return (
    <div className="w-full">
      {policies.map((p) => (
        <div key={p.id} className="bg-white p-3 rounded border mb-2">
          <h4 className="font-semibold text-pink-700">{p.title}</h4>
          <p className="text-sm text-gray-500">{p.snippet}</p>
          <div className="mt-2 text-right">
            <button className="text-sm text-white bg-pink-600 px-3 py-1 rounded">Read</button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 8. Community & Collaboration (challenges, hackathons) */
const CommunityCollab = () => {
  const [challenges] = useState([
    { id: 1, title: "Low-cost Home Storage Solutions", prize: 50000, deadline: "2025-11-30" },
    { id: 2, title: "Child-Friendly Learning Apps", prize: 30000, deadline: "2025-10-10" },
  ]);
  return (
    <div className="w-full">
      {challenges.map((c) => (
        <div key={c.id} className="bg-white p-3 rounded border mb-2 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
          <div className="flex-1">
            <h4 className="font-semibold text-pink-700">{c.title}</h4>
            <p className="text-sm text-gray-500">Deadline: {c.deadline}</p>
          </div>
          <div className="text-right sm:text-left">
            <div className="text-pink-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {c.prize}</div>
            <button className="w-full sm:w-40 mt-2 bg-pink-600 text-white px-3 py-1 rounded">Participate</button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 9. Smart Tools (simple AI Advisor simulation) */
const SmartTools = () => {
  const [q, setQ] = useState("");
  const [resp, setResp] = useState("");
  const ask = (e) => {
    e.preventDefault();
    // simple heuristic responses (dummy)
    const t = q.toLowerCase();
    let r = "Try our Knowledge Zone or request a sample.";
    if (t.includes("craft")) r = "Suggested: 'CraftKit Pro' (trial kit). Ideal for beginners in home crafting.";
    if (t.includes("child")) r = "Focus on interactive play; consider 'LearnFun' app for ages 3-6.";
    if (t.includes("budget")) r = "Start with envelope system; apps like 'FamilyBudget' track expenses easily.";
    setResp(r);
  };

  return (
    <div className="w-full">
      <form onSubmit={ask} className="flex flex-col sm:flex-row gap-2 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask (e.g., best home crafting tool)" className="flex-1 w-full p-2 border border-pink-300 rounded text-black" />
        <button type="submit" className="bg-pink-600 text-white px-3 py-2 rounded">Ask</button>
      </form>
      {resp && <div className="bg-white p-3 rounded border text-sm text-pink-700">{resp}</div>}
    </div>
  );
};

/* 10. Discussion Circles (simple threads) */
const DiscussionCircles = () => {
  const [threads, setThreads] = useState([
    { id: 1, title: "Best home organization tips", posts: 4 },
    { id: 2, title: "Parenting and child care ideas", posts: 9 },
  ]);
  const [title, setTitle] = useState("");
  const create = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setThreads((t) => [{ id: Date.now(), title, posts: 0 }, ...t]);
    setTitle("");
  };

  return (
    <div className="w-full">
      <form onSubmit={create} className="mb-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Start a discussion (thread)" className="w-full p-2 border border-pink-300 rounded text-black" />
      </form>
      <div className="space-y-2">
        {threads.map((th) => (
          <div key={th.id} className="bg-white p-3 rounded border flex flex-col sm:flex-row sm:justify-between gap-2">
            <div className="flex-1">
              <h4 className="font-semibold text-pink-700">{th.title}</h4>
              <p className="text-sm text-gray-500">{th.posts} posts</p>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-full sm:w-24 text-sm bg-pink-600 text-white py-1 rounded">Open</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------- Innovation Hub Container ------------------- */

export default function SheEarnsInnovationHub() {
  const [active, setActive] = useState(null);

  const useCases = [
    { id: "knowledge", title: "Knowledge Zone", desc: "Wiki, explainers & curated research", icon: faBook, component: <KnowledgeZone /> },
    { id: "products", title: "Innovative Products", desc: "Launchpad (0–2 years) & reviews", icon: faRocket, component: <InnovativeProducts /> },
    { id: "services", title: "Innovative Services", desc: "Home services, family advisory", icon: faTools, component: <InnovativeServices /> },
    { id: "feedback", title: "Women Feedback & Trials", desc: "Home trial reports & ratings", icon: faComments, component: <FeedbackTrials /> },
    { id: "samples", title: "Sample Ask & Delivery", desc: "Request samples & track status", icon: faBoxOpen, component: <SampleAsk /> },
    { id: "accelerator", title: "Innovation Accelerator", desc: "Pitch, investors & funding", icon: faHandshake, component: <Accelerator /> },
    { id: "policy", title: "Policy & Schemes", desc: "Subsidies, support for women & families", icon: faGavel, component: <PolicyCorner /> },
    { id: "community", title: "Community & Challenges", desc: "Hackathons & collaboration", icon: faUsers, component: <CommunityCollab /> },
    { id: "smart", title: "Smart Tools (AI Advisor)", desc: "Ask an AI advisor for quick suggestions", icon: faRobot, component: <SmartTools /> },
    { id: "circles", title: "Discussion Circles", desc: "Home & family discussion threads", icon: faCommentsDollar, component: <DiscussionCircles /> },
  ];

  return (
    <div className="min-h-screen">
      <section className="min-h-screen bg-gray-800 py-4 px-4 sm:py-2 sm:px-2 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-6 sm:mb-8">
            <h1 className="text-base sm:text-lg md:text-xl font-bold text-pink-100 flex items-center justify-center gap-2 sm:gap-3 mx-auto max-w-2xl">
              <span className="text-lg sm:text-xl bg-pink-100 text-pink-700 rounded-full w-6 h-6 flex items-center justify-center">💕</span>
              SAKSHAM Shakti Innovation Hub
            </h1>
            <p className="mt-3 text-xs sm:text-sm text-white max-w-3xl mx-auto px-2">
              Discover and pilot breakthrough innovations for women, home, children, and family. Connect with startups, request samples, and collaborate with investors and communities.
            </p>
          </header>

          {/* Innovative Product Launched Section */}
          <div className="bg-gray-900 border border-pink-600 rounded-2xl shadow-sm hover:shadow-md transition p-4 sm:p-6 mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-pink-100 mb-4 sm:mb-6 text-center">
              🚀 Innovative Product Launched
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Product Card 1 */}
              <div className="bg-white rounded-xl shadow p-4 sm:p-5 flex flex-col justify-between">
                <div>
                  <img
                    src="https://via.placeholder.com/300x180.png?text=Smart+Home+Assistant"
                    alt="Smart Home Assistant"
                    className="w-full h-32 sm:h-40 object-cover rounded-lg mb-3 sm:mb-4"
                  />
                  <h3 className="text-base sm:text-lg font-semibold text-pink-700">Smart Home Assistant</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Voice-activated helper for managing home tasks and family schedules.
                  </p>
                </div>
                <div className="mt-3 sm:mt-4 flex flex-row justify-center items-center sm:flex-row gap-2">
                  <button className="flex-1 bg-pink-600 hover:bg-pink-700 text-white py-2 rounded-lg text-sm">
                    Enquiry
                  </button>
                  <button className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-2 rounded-lg text-sm">
                    Order
                  </button>
                  <button className="flex-1 bg-pink-400 hover:bg-pink-500 text-white py-2 rounded-lg text-sm">
                    Contact
                  </button>
                </div>
              </div>

              {/* Product Card 2 */}
              <div className="bg-white rounded-xl shadow p-4 sm:p-5 flex flex-col justify-between">
                <div>
                  <img
                    src="https://via.placeholder.com/300x180.png?text=Child+Learning+App"
                    alt="Child Learning App"
                    className="w-full h-32 sm:h-40 object-cover rounded-lg mb-3 sm:mb-4"
                  />
                  <h3 className="text-base sm:text-lg font-semibold text-pink-700">Child Learning App</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Interactive app for children's education and skill development.
                  </p>
                </div>
                <div className="mt-3 sm:mt-4 flex flex-row justify-center items-center sm:flex-row gap-2">
                  <button className="flex-1 bg-pink-600 hover:bg-pink-700 text-white py-2 rounded-lg text-sm">
                    Enquiry
                  </button>
                  <button className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-2 rounded-lg text-sm">
                    Order
                  </button>
                  <button className="flex-1 bg-pink-400 hover:bg-pink-500 text-white py-2 rounded-lg text-sm">
                    Contact
                  </button>
                </div>
              </div>

              {/* Product Card 3 */}
              <div className="bg-white rounded-xl shadow p-4 sm:p-5 flex flex-col justify-between">
                <div>
                  <img
                    src="https://via.placeholder.com/300x180.png?text=Family+Health+Tracker"
                    alt="Family Health Tracker"
                    className="w-full h-32 sm:h-40 object-cover rounded-lg mb-3 sm:mb-4"
                  />
                  <h3 className="text-base sm:text-lg font-semibold text-pink-700">Family Health Tracker</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Wearable device for monitoring family wellness and activity.
                  </p>
                </div>
                <div className="mt-3 sm:mt-4 flex flex-row justify-center items-center sm:flex-row gap-2">
                  <button className="flex-1 bg-pink-600 hover:bg-pink-700 text-white py-2 rounded-lg text-sm">
                    Enquiry
                  </button>
                  <button className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-2 rounded-lg text-sm">
                    Order
                  </button>
                  <button className="flex-1 bg-pink-400 hover:bg-pink-500 text-white py-2 rounded-lg text-sm">
                    Contact
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 place-items-center">
            {useCases.map((u) => (
              <Portlet key={u.id} icon={u.icon} title={u.title} desc={u.desc} expanded={active === u.id} onToggle={() => setActive((s) => (s === u.id ? null : u.id))}>
                {u.component}
                <div className="mt-3 sm:mt-4 text-xs text-pink-100">Tip: Click to expand — all content is dummy.</div>
              </Portlet>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-pink-900 text-white py-8 sm:py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col sm:flex-row justify-between items-center text-center sm:text-left gap-4">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:space-x-6 text-sm">
            <Link to="/about-us" className="text-white hover:text-pink-300 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-pink-300 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-pink-300 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-pink-300 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}